

import { useState, useEffect } from "react"
import { useHistory, useParams } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { Formik } from "formik"
import * as Yup from "yup"
import RichTextEditor from "../../../components/admin/common/RichTextEditor"
import FileUpload from "../../../components/admin/common/FileUpload"
import SpinnerLoader from "../../../components/common/SpinnerLoader"
import Header from "../../../components/admin/common/Header"

const EditLegalDocument = () => {
  const [loading, setLoading] = useState(true)
  const [document, setDocument] = useState(null)
  const [editorContent, setEditorContent] = useState("")
  const [uploadedFile, setUploadedFile] = useState(null)

  const { documentId } = useParams()
  const history = useHistory()
  const authInfo = useSelector((state) => state.auth.authInfo)

  const categoryOptions = [
    { value: "terms_conditions", label: "Terms & Conditions" },
    { value: "privacy_policy", label: "Privacy Policy" },
    { value: "disclaimer", label: "Disclaimer" },
    { value: "ip_management", label: "IP Management" },
    { value: "cookie_policy", label: "Cookie Policy" },
    { value: "refund_policy", label: "Refund Policy" },
    { value: "community_guidelines", label: "Community Guidelines" },
    { value: "seller_agreement", label: "Seller Agreement" },
    { value: "buyer_agreement", label: "Buyer Agreement" },
    { value: "api_terms", label: "API Terms" },
  ]

  const typeOptions = [
    { value: "rich_text", label: "Rich Text Editor" },
    { value: "markdown", label: "Markdown" },
    { value: "html", label: "HTML" },
    { value: "static", label: "Static Text" },
    { value: "custom_upload", label: "File Upload" },
  ]

  const ipCategoryOptions = [
    { value: "stripe_related", label: "Stripe Related" },
    { value: "social_media", label: "Social Media" },
    { value: "signup_related", label: "Signup Related" },
    { value: "platform_assets", label: "Platform Assets" },
    { value: "logo_usage", label: "Logo Usage" },
    { value: "code_usage", label: "Code Usage" },
    { value: "trademark", label: "Trademark" },
    { value: "copyright", label: "Copyright" },
    { value: "data_protection", label: "Data Protection" },
    { value: "third_party_integrations", label: "Third Party Integrations" },
  ]

  const visibilityScopeOptions = [
    { value: "public", label: "Public" },
    { value: "buyer", label: "Buyers Only" },
    { value: "seller", label: "Sellers Only" },
    { value: "admin", label: "Admin Only" },
    { value: "law_body", label: "Law Body Only" },
  ]

  const agreementTriggerOptions = [
    { value: "signup", label: "User Signup" },
    { value: "service_creation", label: "Service Creation" },
    { value: "order_placement", label: "Order Placement" },
    { value: "order_acceptance", label: "Order Acceptance" },
    { value: "payment_processing", label: "Payment Processing" },
    { value: "profile_completion", label: "Profile Completion" },
    { value: "seller_verification", label: "Seller Verification" },
    { value: "api_access", label: "API Access" },
  ]

  useEffect(() => {
    fetchDocument()
  }, [documentId])

  const fetchDocument = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`admin/legaldocument/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        const doc = response.data.data
        setDocument(doc)
        setEditorContent(doc.content || "")
      }
    } catch (error) {
      console.error("Error fetching document:", error)
      toast.error("Failed to fetch document")
      history.push("/admin/legal-documents")
    } finally {
      setLoading(false)
    }
  }

  const validationSchema = Yup.object({
    title: Yup.string()
      .min(5, "Title must be at least 5 characters")
      .max(200, "Title must not exceed 200 characters")
      .required("Title is required"),
    category: Yup.string().required("Category is required"),
    type: Yup.string().required("Document type is required"),
    visibilityScope: Yup.array().min(1, "At least one visibility scope is required"),
    ipCategory: Yup.string().when("category", {
      is: "ip_management",
      then: Yup.string().required("IP category is required for IP management documents"),
    }),
    effectiveFrom: Yup.date().required("Effective date is required"),
  })

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true)

      const formData = new FormData()

      // Add all form fields
      Object.keys(values).forEach((key) => {
        if (key === "visibilityScope" || key === "agreementTriggers") {
          values[key].forEach((item) => {
            formData.append(`${key}[]`, item)
          })
        } else if (key === "seoTags") {
          Object.keys(values[key]).forEach((seoKey) => {
            if (values[key][seoKey]) {
              formData.append(`seoTags.${seoKey}`, values[key][seoKey])
            }
          })
        } else if (key === "complianceInfo") {
          Object.keys(values[key]).forEach((compKey) => {
            if (values[key][compKey]) {
              formData.append(`complianceInfo.${compKey}`, values[key][compKey])
            }
          })
        } else if (values[key] !== undefined && values[key] !== "") {
          formData.append(key, values[key])
        }
      })

      // Add content based on type
      if (values.type === "custom_upload" && uploadedFile) {
        formData.append("file", uploadedFile)
      } else {
        formData.append("content", editorContent)
      }

      // Add versioning options
      formData.append("createNewVersion", values.createNewVersion || false)
      formData.append("majorVersionChange", values.majorVersionChange || false)

      const response = await axios.put(`admin/legaldocument/documents/${documentId}`, formData, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
          "Content-Type": "multipart/form-data",
        },
      })

      if (response.data.status) {
        toast.success("Legal document updated successfully")
        history.push("/admin/legal-documents")
      }
    } catch (error) {
      console.error("Error updating document:", error)
      toast.error(error.response?.data?.message || "Failed to update document")
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  if (loading || !document) {
    return <SpinnerLoader />
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    return new Date(dateString).toISOString().split("T")[0]
  }

  return (
    <>
    <Header />
    <div className="edit-legal-document" style={{ minHeight: "100vh", overflowY: "auto", padding: '18px 100px' }}>
      <div className="container-fluid" style={{ paddingBottom: "2rem" }}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">Edit Legal Document</h2>
                <p className="text-muted">Update legal content, terms, or IP agreements</p>
              </div>
              <div>
                <button
                  onClick={() => history.push(`/admin/legal-documents/view/${documentId}`)}
                  className="btn btn-outline-info me-2"
                >
                  View
                </button>
                <button onClick={() => history.goBack()} className="btn btn-outline-secondary">
                  Back
                </button>
              </div>
            </div>

            <Formik
              initialValues={{
                title: document.title || "",
                category: document.category || "",
                type: document.type || "rich_text",
                visibilityScope: document.visibilityScope || ["public"],
                ipCategory: document.ipCategory || "",
                requiresAgreement: document.requiresAgreement || false,
                agreementTriggers: document.agreementTriggers || [],
                effectiveFrom: formatDate(document.effectiveFrom),
                expiresAt: formatDate(document.expiresAt),
                adminNotes: document.adminNotes || "",
                seoTags: {
                  metaTitle: document.seoTags?.metaTitle || "",
                  metaDescription: document.seoTags?.metaDescription || "",
                  keywords: document.seoTags?.keywords?.join(", ") || "",
                  canonicalUrl: document.seoTags?.canonicalUrl || "",
                },
                complianceInfo: {
                  jurisdiction: document.complianceInfo?.jurisdiction || "",
                  lawReference: document.complianceInfo?.lawReference || "",
                  nextReviewDate: formatDate(document.complianceInfo?.nextReviewDate),
                },
                createNewVersion: false,
                majorVersionChange: false,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-8">
                      {/* Version Control Alert */}
                      <div className="alert alert-info">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-info-circle me-2"></i>
                          <div className="flex-grow-1">
                            <strong>Current Version: {document.version}</strong>
                            <p className="mb-0 small">
                              Last modified by {document.lastModifiedBy?.name} on{" "}
                              {new Date(document.updatedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="createNewVersion"
                              name="createNewVersion"
                              checked={values.createNewVersion}
                              onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="createNewVersion">
                              Create new version (recommended for significant changes)
                            </label>
                          </div>
                          {values.createNewVersion && (
                            <div className="form-check mt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="majorVersionChange"
                                name="majorVersionChange"
                                checked={values.majorVersionChange}
                                onChange={handleChange}
                              />
                              <label className="form-check-label" htmlFor="majorVersionChange">
                                Major version change (will invalidate existing user agreements)
                              </label>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="card">
                        <div className="card-header">
                          <h5 className="mb-0">Document Details</h5>
                        </div>
                        <div className="card-body">
                          {/* Title */}
                          <div className="mb-3">
                            <label className="form-label">
                              Document Title <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${touched.title && errors.title ? "is-invalid" : ""}`}
                              name="title"
                              value={values.title}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter document title"
                            />
                            {touched.title && errors.title && <div className="invalid-feedback">{errors.title}</div>}
                          </div>

                          {/* Category and Type */}
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Category <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${touched.category && errors.category ? "is-invalid" : ""}`}
                                  name="category"
                                  value={values.category}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  <option value="">Select Category</option>
                                  {categoryOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {touched.category && errors.category && (
                                  <div className="invalid-feedback">{errors.category}</div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Document Type <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${touched.type && errors.type ? "is-invalid" : ""}`}
                                  name="type"
                                  value={values.type}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  {typeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {touched.type && errors.type && <div className="invalid-feedback">{errors.type}</div>}
                              </div>
                            </div>
                          </div>

                          {/* IP Category (conditional) */}
                          {values.category === "ip_management" && (
                            <div className="mb-3">
                              <label className="form-label">
                                IP Category <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-select ${touched.ipCategory && errors.ipCategory ? "is-invalid" : ""}`}
                                name="ipCategory"
                                value={values.ipCategory}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select IP Category</option>
                                {ipCategoryOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {touched.ipCategory && errors.ipCategory && (
                                <div className="invalid-feedback">{errors.ipCategory}</div>
                              )}
                            </div>
                          )}

                          {/* Content Editor */}
                          <div className="mb-3">
                            <label className="form-label">
                              Content <span className="text-danger">*</span>
                            </label>
                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                              {values.type === "custom_upload" ? (
                                <div>
                                  {document.uploadedFile && (
                                    <div className="alert alert-info mb-3">
                                      <i className="fas fa-file me-2"></i>
                                      Current file: <strong>{document.uploadedFile.fileName}</strong>
                                      <br />
                                      <small>Upload a new file to replace the current one</small>
                                    </div>
                                  )}
                                  <FileUpload
                                    onFileSelect={setUploadedFile}
                                    acceptedTypes=".pdf,.doc,.docx,.txt,.html,.md"
                                    maxSize={50 * 1024 * 1024} // 50MB
                                  />
                                </div>
                              ) : (
                                <RichTextEditor
                                  value={editorContent}
                                  onChange={setEditorContent}
                                  placeholder="Enter document content..."
                                  type={values.type}
                                />
                              )}
                            </div>
                          </div>

                          {/* Admin Notes */}
                          <div className="mb-3">
                            <label className="form-label">Admin Notes</label>
                            <textarea
                              className="form-control"
                              name="adminNotes"
                              value={values.adminNotes}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              rows="3"
                              placeholder="Internal notes about this document..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* SEO Settings */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">SEO Settings</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Meta Title</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="seoTags.metaTitle"
                                  value={values.seoTags.metaTitle}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="SEO meta title"
                                  maxLength="60"
                                />
                                <small className="text-muted">{values.seoTags.metaTitle.length}/60 characters</small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Canonical URL</label>
                                <input
                                  type="url"
                                  className="form-control"
                                  name="seoTags.canonicalUrl"
                                  value={values.seoTags.canonicalUrl}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="https://example.com/legal/document"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Meta Description</label>
                            <textarea
                              className="form-control"
                              name="seoTags.metaDescription"
                              value={values.seoTags.metaDescription}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              rows="2"
                              placeholder="SEO meta description"
                              maxLength="160"
                            />
                            <small className="text-muted">{values.seoTags.metaDescription.length}/160 characters</small>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Keywords</label>
                            <input
                              type="text"
                              className="form-control"
                              name="seoTags.keywords"
                              value={values.seoTags.keywords}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="keyword1, keyword2, keyword3"
                            />
                            <small className="text-muted">Separate keywords with commas</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      {/* Document Info */}
                      <div className="card">
                        <div className="card-header">
                          <h5 className="mb-0">Document Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="text-muted small">Document Code</label>
                            <div className="fw-bold">{document.documentCode}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Current Version</label>
                            <div className="fw-bold">{document.version}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Created By</label>
                            <div>{document.createdBy?.name}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Created At</label>
                            <div>{new Date(document.createdAt).toLocaleDateString()}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Last Modified</label>
                            <div>{new Date(document.updatedAt).toLocaleDateString()}</div>
                          </div>
                          <div className="mb-3">
                            <label className="text-muted small">Word Count</label>
                            <div>{document.wordCount || 0}</div>
                          </div>
                          {document.estimatedReadTime > 0 && (
                            <div className="mb-3">
                              <label className="text-muted small">Estimated Read Time</label>
                              <div>{document.estimatedReadTime} minutes</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Publishing Settings */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">Publishing Settings</h5>
                        </div>
                        <div className="card-body">
                          {/* Visibility Scope */}
                          <div className="mb-3">
                            <label className="form-label">
                              Visibility Scope <span className="text-danger">*</span>
                            </label>
                            {visibilityScopeOptions.map((option) => (
                              <div key={option.value} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`visibility-${option.value}`}
                                  checked={values.visibilityScope.includes(option.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue("visibilityScope", [...values.visibilityScope, option.value])
                                    } else {
                                      setFieldValue(
                                        "visibilityScope",
                                        values.visibilityScope.filter((v) => v !== option.value),
                                      )
                                    }
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`visibility-${option.value}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>

                          {/* Effective Dates */}
                          <div className="mb-3">
                            <label className="form-label">
                              Effective From <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control ${touched.effectiveFrom && errors.effectiveFrom ? "is-invalid" : ""}`}
                              name="effectiveFrom"
                              value={values.effectiveFrom}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.effectiveFrom && errors.effectiveFrom && (
                              <div className="invalid-feedback">{errors.effectiveFrom}</div>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Expires At</label>
                            <input
                              type="date"
                              className="form-control"
                              name="expiresAt"
                              value={values.expiresAt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <small className="text-muted">Leave empty for no expiration</small>
                          </div>
                        </div>
                      </div>

                      {/* Agreement Settings */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">Agreement Settings</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="requiresAgreement"
                                name="requiresAgreement"
                                checked={values.requiresAgreement}
                                onChange={handleChange}
                              />
                              <label className="form-check-label" htmlFor="requiresAgreement">
                                Requires User Agreement
                              </label>
                            </div>
                          </div>

                          {values.requiresAgreement && (
                            <div className="mb-3">
                              <label className="form-label">Agreement Triggers</label>
                              {agreementTriggerOptions.map((option) => (
                                <div key={option.value} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`trigger-${option.value}`}
                                    checked={values.agreementTriggers.includes(option.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFieldValue("agreementTriggers", [...values.agreementTriggers, option.value])
                                      } else {
                                        setFieldValue(
                                          "agreementTriggers",
                                          values.agreementTriggers.filter((t) => t !== option.value),
                                        )
                                      }
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`trigger-${option.value}`}>
                                    {option.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Compliance Info */}
                      <div className="card mt-4">
                        <div className="card-header">
                          <h5 className="mb-0">Compliance Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label">Jurisdiction</label>
                            <input
                              type="text"
                              className="form-control"
                              name="complianceInfo.jurisdiction"
                              value={values.complianceInfo.jurisdiction}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g., US, EU, Global"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Law Reference</label>
                            <input
                              type="text"
                              className="form-control"
                              name="complianceInfo.lawReference"
                              value={values.complianceInfo.lawReference}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g., GDPR Article 13"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Next Review Date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="complianceInfo.nextReviewDate"
                              value={values.complianceInfo.nextReviewDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="card mt-4">
                        <div className="card-body">
                          <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting || loading}>
                            {isSubmitting || loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Updating...
                              </>
                            ) : (
                              <>
                                Update Document
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default EditLegalDocument